import { useState, useEffect } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import NotificationItem from "./NotificationItem";
import NotificationDetailModal from "./NotificationDetailModal";
import { toast } from "@/hooks/use-toast";

interface AdminNotification {
  id: string;
  title_it: string;
  title_en: string | null;
  title_es: string | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
  image_url: string | null;
  cta_url: string | null;
  cta_text_it: string | null;
  cta_text_en: string | null;
  cta_text_es: string | null;
  created_at: string;
}

interface UserNotification {
  id: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  notification: AdminNotification;
}

export default function NotificationCenter() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<UserNotification | null>(null);

  useEffect(() => {
    if (user) {
      loadNotifications();
      const cleanup = subscribeToNotifications();
      return cleanup;
    }
  }, [user]);

  const loadNotifications = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_notifications")
        .select(`
          id,
          is_read,
          read_at,
          created_at,
          notification:admin_notifications (
            id,
            title_it,
            title_en,
            title_es,
            description_it,
            description_en,
            description_es,
            image_url,
            cta_url,
            cta_text_it,
            cta_text_en,
            cta_text_es,
            created_at
          )
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;

      // Filter out entries where notification is null (deleted admin notifications)
      const validNotifications = (data || []).filter(
        (n): n is UserNotification => n.notification !== null
      );

      setNotifications(validNotifications);
      setUnreadCount(validNotifications.filter((n) => !n.is_read).length);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToNotifications = () => {
    if (!user) return () => {};

    const channel = supabase
      .channel("user-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        async () => {
          // Reload notifications to get the full data with join
          await loadNotifications();
          toast.info(t("notifications.newNotification", "Nuova notifica"));
        }
      )
      .subscribe((status, err) => {
        // Silently handle subscription errors to prevent console noise
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Connection failed silently - realtime updates will not work but app functions normally
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("user_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) throw error;

      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
      toast.success(t("notifications.allMarkedRead", "Tutte le notifiche segnate come lette"));
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error(t("notifications.markReadError", "Errore nel segnare come lette"));
    }
  };

  const toggleReadStatus = async (notificationId: string, currentIsRead: boolean) => {
    try {
      const newIsRead = !currentIsRead;
      const { error } = await supabase
        .from("user_notifications")
        .update({ 
          is_read: newIsRead, 
          read_at: newIsRead ? new Date().toISOString() : null 
        })
        .eq("id", notificationId);

      if (error) throw error;

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, is_read: newIsRead, read_at: newIsRead ? new Date().toISOString() : null }
            : n
        )
      );
      setUnreadCount((prev) => newIsRead ? Math.max(0, prev - 1) : prev + 1);
    } catch (error) {
      console.error("Error toggling notification read status:", error);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("user_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId);

      if (error) throw error;

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleNotificationClick = (notification: UserNotification) => {
    setSelectedNotification(notification);
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  const handleCloseModal = () => {
    setSelectedNotification(null);
  };

  if (!user) return null;

  return (
    <div className="relative">
      {/* Bell icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-surface rounded-lg transition-colors"
        aria-label={t("notifications.notifications", "Notifiche")}
      >
        <Bell size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed left-0 right-0 top-[75px] w-full md:absolute md:right-0 md:left-auto md:top-full md:mt-2 md:w-96 bg-card border-[3px] border-dark md:shadow-[8px_8px_0px_0px] md:shadow-dark max-h-[500px] overflow-auto z-50">
            <div className="p-4 border-b-[3px] border-dark flex justify-between items-center sticky top-0 bg-card">
              <h3 className="font-heading text-lg font-bold">
                {t("notifications.notifications", "Notifiche")}
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-dark font-bold hover:underline flex items-center gap-1.5"
                >
                  <CheckCheck className="h-4 w-4" />
                  {t("notifications.markAllRead", "Segna tutte come lette")}
                </button>
              )}
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {t("notifications.noNotifications", "Nessuna notifica")}
              </div>
            ) : (
              <div>
                {notifications.map((notif) => (
                  <NotificationItem
                    key={notif.id}
                    notification={notif}
                    onClick={() => handleNotificationClick(notif)}
                    onToggleRead={() => toggleReadStatus(notif.id, notif.is_read)}
                  />
                ))}
              </div>
            )}

            <div className="p-3 border-t-[3px] border-dark text-center sticky bottom-0 bg-card">
              <Link
                to="/dashboard/notifications"
                className="text-sm text-foreground font-bold hover:underline"
                onClick={() => setIsOpen(false)}
              >
                {t("notifications.viewAll", "Vedi tutte le notifiche")}
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Detail Modal */}
      <NotificationDetailModal
        notification={selectedNotification?.notification || null}
        isOpen={!!selectedNotification}
        onClose={handleCloseModal}
      />
    </div>
  );
}
